import React from "react";

/**
 * THEME "sobre premium" — composants d'affichage réutilisables.
 * Purement visuels : aucun état ni logique métier ici.
 * Voir `index.css` (layer components) pour les classes .th-*.
 */

/* ── Card ─────────────────────────────────────────────────────── */
export function Card({ className = "", children, ...rest }) {
  return (
    <div className={`th-card ${className}`} {...rest}>
      {children}
    </div>
  );
}

/* ── SectionLabel (12px petites majuscules discrètes) ─────────── */
export function SectionLabel({ className = "", children, ...rest }) {
  return (
    <div className={`th-section-label ${className}`} {...rest}>
      {children}
    </div>
  );
}

/* ── Meta text 12px ───────────────────────────────────────────── */
export function Meta({ className = "", children, ...rest }) {
  return (
    <div className={`th-meta ${className}`} {...rest}>
      {children}
    </div>
  );
}

/* ── StatusPill ───────────────────────────────────────────────── */
const PILL_MAP = {
  HAUT: { cls: "th-pill--haut", label: "Haut" },
  MOYEN: { cls: "th-pill--moyen", label: "Moyen" },
  FAIBLE: { cls: "th-pill--faible", label: "Faible" },
  NEUTRAL: { cls: "th-pill--neutral", label: "" },
};

export function StatusPill({ level = "NEUTRAL", children, className = "", ...rest }) {
  const cfg = PILL_MAP[level] || PILL_MAP.NEUTRAL;
  return (
    <span className={`th-pill ${cfg.cls} ${className}`} {...rest}>
      {children ?? cfg.label}
    </span>
  );
}

/* ── Tag neutre (client dormant / à risque / panier baisse) ───── */
export function NeutralTag({ children, className = "", ...rest }) {
  return (
    <span className={`th-pill th-pill--neutral ${className}`} {...rest}>
      {children}
    </span>
  );
}

/* ── Button (primary / secondary / ghost) ─────────────────────── */
export function Button({
  variant = "secondary",
  className = "",
  children,
  as: As = "button",
  ...rest
}) {
  const v =
    variant === "primary" ? "th-btn--primary" :
    variant === "ghost" ? "th-btn--ghost" :
    "th-btn--secondary";
  return (
    <As className={`th-btn ${v} ${className}`} {...rest}>
      {children}
    </As>
  );
}

/* ── ListRow (ligne active + filet gauche navy) ───────────────── */
export function ListRow({ active, onClick, className = "", children, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`th-listrow ${active ? "is-active" : ""} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ── MetricCard ───────────────────────────────────────────────── */
export function MetricCard({ label, value, hint, className = "", ...rest }) {
  return (
    <div className={`th-metric ${className}`} {...rest}>
      <div className="th-metric__label">{label}</div>
      <div className="th-metric__value">{value}</div>
      {hint && <div className="th-meta mt-1">{hint}</div>}
    </div>
  );
}

/* ── Table (utilitaire) ───────────────────────────────────────── */
export function Table({ className = "", children, ...rest }) {
  return (
    <table className={`th-table ${className}`} {...rest}>
      {children}
    </table>
  );
}

/* ── Kbd (raccourci clavier) ──────────────────────────────────── */
export function Kbd({ children, className = "", ...rest }) {
  return (
    <span className={`th-kbd ${className}`} {...rest}>
      {children}
    </span>
  );
}
